import { useContext } from 'react';
import { Can } from '@/components/common/Can';
import { BILLING_PERMISSIONS } from '@/constants/permissions';
import { OptionSelect } from '@/components/common/OptionSelect';
import { parseNumberText } from '@/utils/number';
import { AmountInput } from './AmountInput';
import { QueueDecisionButtons } from './QueueDecisionButtons';
import { DcrDraftsContext } from './queueDraftsContext';
import { VerifyStatusBadge } from './VerifyStatusBadge';
import type { OfflineDcrRow, TransportType, BillingLimits } from '../types/billing.types';
import {
  amountProblem,
  dcrVerifiability,
  getAmountCap,
  getStartingTransportTypeId,
} from '../utils/verifiability';

export interface DcrQueueColumnDeps {
  mode: 'verify' | 'approve';
  limits: BillingLimits | undefined;
  transportTypes: TransportType[] | undefined;
  transportTypesLoading: boolean;
  onDecide: (row: OfflineDcrRow, accept: boolean) => void;
  onOpenDetails: (recordId: number) => void;
  pendingRecordId: number | null;
  searchText: string | undefined;
}

interface DcrQueueCellProps {
  row: OfflineDcrRow;
  deps: DcrQueueColumnDeps;
}

export function DcrTransportCell({ row, deps }: DcrQueueCellProps) {
  const { drafts, onDraftChange } = useContext(DcrDraftsContext);
  const draft = drafts[row.recordId];
  const settled =
    deps.mode === 'verify' ? row.status !== 'pending' : row.approveStatus !== 'pending';

  return (
    <div className="w-40">
      <OptionSelect
        options={deps.transportTypes}
        isLoading={deps.transportTypesLoading}
        value={draft?.transportTypeId ?? getStartingTransportTypeId(deps.mode, row) ?? undefined}
        onChange={(transportTypeId) => onDraftChange(row.recordId, { transportTypeId })}
        placeholder="Not set"
        disabled={settled}
      />
    </div>
  );
}

export function DcrFareCell({ row, deps }: DcrQueueCellProps) {
  const { drafts, onDraftChange } = useContext(DcrDraftsContext);
  const { limits } = deps;
  if (!limits) return null;
  const fareCap = getAmountCap(deps.mode, row.fareAmount, row.approveFareAmount);
  const value = drafts[row.recordId]?.approveFareAmount ?? String(fareCap.amount);
  const settled =
    deps.mode === 'verify' ? row.status !== 'pending' : row.approveStatus !== 'pending';

  return (
    <AmountInput
      id={`dcr-fare-${row.recordId}`}
      label={`Fare to approve for ${row.name}`}
      value={value}
      cap={fareCap}
      disabled={settled}
      onChange={(next) => onDraftChange(row.recordId, { approveFareAmount: next })}
      problem={
        settled ? undefined : amountProblem(parseNumberText(value), fareCap, limits.dcrFare, 'fare')
      }
    />
  );
}

export function DcrDecisionCell({ row, deps }: DcrQueueCellProps) {
  const { drafts } = useContext(DcrDraftsContext);
  const { limits } = deps;
  if (!limits) return null;
  const { canDecide, reason } = dcrVerifiability(row, limits, deps.mode);
  const fareCap = getAmountCap(deps.mode, row.fareAmount, row.approveFareAmount);
  const value = drafts[row.recordId]?.approveFareAmount ?? String(fareCap.amount);
  const problem = amountProblem(parseNumberText(value), fareCap, limits.dcrFare, 'fare');
  const busy = deps.pendingRecordId === row.recordId;
  const settled =
    deps.mode === 'verify' ? row.status !== 'pending' : row.approveStatus !== 'pending';

  if (settled) {
    return (
      <VerifyStatusBadge
        status={deps.mode === 'verify' ? row.status : row.approveStatus}
        decisionKind={deps.mode}
      />
    );
  }

  return (
    <Can
      code={
        deps.mode === 'verify'
          ? BILLING_PERMISSIONS.OFFLINE_DCR_VERIFY_VERIFY
          : BILLING_PERMISSIONS.APPROVAL_APPROVE
      }
    >
      <QueueDecisionButtons
        acceptLabel={deps.mode === 'verify' ? 'Verify' : 'Approve'}
        isAcceptDisabled={!canDecide || problem !== undefined || busy}
        isBusy={busy}
        acceptHint={reason ?? problem}
        reason={reason}
        onAccept={() => deps.onDecide(row, true)}
        onReject={() => deps.onDecide(row, false)}
      />
    </Can>
  );
}
